import { useState } from 'react';
import type { RefundMethod, ReturnDto } from '@ecom/contracts';
import { useReturnsStore } from '@/stores/returns.store';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { money } from '@/lib/format';
import { halalasToRiyals } from '@/lib/price-input';
import { paidByHand } from '@/lib/payment-state';
import { checkRefundAmount, creditableLeft, refundCap, refundPayment } from '@/lib/returns';
import { Choices } from '@/components/fields';
import { Sheet } from '@/components/page';
import type { MessageKey } from '@/i18n/messages';
import { Alert, Button, ConfirmDialog, Field, Input, Select, Skeleton } from '@/components/ui';

const REFUND_METHODS: RefundMethod[] = ['BANK_TRANSFER', 'CASH', 'OTHER'];

/**
 * Refunding a return — the one step in the admin that sends money out.
 *
 * It opens on the whole amount, which is nearly always right; part of it can
 * be kept back by typing less. Before anything moves, the confirmation says
 * exactly how much goes back and which way: to the card through the provider
 * that took it, or — for cash on delivery, where no provider holds the money —
 * recorded for the owner to pay back by hand, with how it went and a reference
 * (ADR-061) — the API refuses a cash refund without them. The API checks the amount
 * against the return and the payment regardless; the form only refuses early
 * what would be refused anyway.
 */
export function RefundSheet({ row, onClose }: { row: ReturnDto; onClose: () => void }) {
  const { saving, actionError, order, orderLoading, providerNames, refund } = useReturnsStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();

  const linked = order?.id === row.orderId ? order : null;
  const payment = refundPayment(linked?.payments);
  // To the customer's balance (ADR-081): offered to an account, the only way for an order the balance paid.
  const creditable = Boolean(linked?.customer?.id);
  const creditOnly = payment?.provider === 'wallet';
  const [toCredit, setToCredit] = useState(false);
  const credit = creditOnly || (creditable && toCredit);
  const cap = credit ? Math.min(row.amount, creditableLeft(linked?.payments)) : refundCap(row.amount, payment);

  // Untouched, the field follows the cap — which may still be arriving with the order.
  const [typed, setTyped] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [method, setMethod] = useState<RefundMethod | ''>('');
  const [reference, setReference] = useState('');
  const value = typed ?? halalasToRiyals(cap);
  const check = checkRefundAmount(value, cap);
  const amount = check.ok ? check.amount : null;

  const shown = amount === null ? '' : money(amount, locale);
  // Cash at the door, or paid outside the store: it goes back by hand (ADR-061, ADR-062).
  const viaCash = linked !== null && paidByHand(linked) && !credit;
  const providerName = payment ? (providerNames?.[payment.provider] ?? payment.provider) : '';
  const statement = credit
    ? t('refundToCredit', { amount: shown })
    : !linked || (!viaCash && !payment)
      ? t('refundToUnknown', { amount: shown })
      : linked.paymentMethod === 'MANUAL'
        ? t('refundToHand', { amount: shown })
        : viaCash
          ? t('refundToCod', { amount: shown })
          : t('refundToCard', { amount: shown, provider: providerName });

  const amountError = check.ok
    ? undefined
    : check.error === 'over'
      ? t('refundAmountOver', { max: money(cap, locale) })
      : check.error === 'zero'
        ? t('refundAmountZero')
        : t('refundAmountInvalid');

  async function confirm() {
    if (amount === null) return;
    // The whole return goes without a figure, so the API refunds exactly its worth.
    const done = await refund(
      row.id,
      amount === row.amount ? undefined : amount,
      credit
        ? { method: 'STORE_CREDIT' }
        : viaCash && method
          ? { method, ...(reference.trim() ? { reference: reference.trim() } : {}) }
          : undefined,
    );
    setConfirming(false);
    if (done) onClose();
  }

  return (
    <>
      <Sheet
        open
        title={t('refundSheetTitle')}
        subtitle={`${row.number} · ${row.productName}`}
        // A window-wide Escape would close the panel from under its own confirmation.
        onClose={() => {
          if (!confirming) onClose();
        }}
        footer={
          <>
            <Button variant="secondary" onClick={onClose} disabled={saving}>
              {t('cancel')}
            </Button>
            <Button
              type="submit"
              form="return-refund"
              disabled={amount === null || saving || orderLoading || (viaCash && method === '')}
            >
              {amount === null ? t('refund') : t('refundConfirm', { amount: shown })}
            </Button>
          </>
        }
      >
        <form
          id="return-refund"
          className="flex flex-col gap-5"
          onSubmit={(event) => {
            event.preventDefault();
            if (amount !== null && !orderLoading && (!viaCash || method !== '')) setConfirming(true);
          }}
        >
          {orderLoading ? (
            <Skeleton className="h-20" />
          ) : linked ? (
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 rounded-control border border-line p-4 text-sm">
              <dt className="text-ink-muted">{t('returnWorth')}</dt>
              <dd data-numeric className="text-ink">
                {money(row.amount, locale)}
              </dd>
              <dt className="text-ink-muted">{t('paymentMethod')}</dt>
              <dd className="text-ink">
                {linked.paymentMethod === 'MANUAL'
                  ? t('payManual')
                  : linked.paymentMethod === 'WALLET'
                    ? t('payWallet')
                    : paidByHand(linked)
                      ? t('paymentCod')
                      : t('paymentCard')}
                {payment && !viaCash ? (
                  <span dir="ltr" className="ms-1 text-xs text-ink-muted">
                    ({providerName})
                  </span>
                ) : null}
              </dd>
              {payment ? (
                <>
                  <dt className="text-ink-muted">{t('total')}</dt>
                  <dd data-numeric className="text-ink">
                    {money(payment.amount, locale, payment.currency)}
                  </dd>
                </>
              ) : null}
              {payment && payment.refundedAmount > 0 ? (
                <>
                  <dt className="text-ink-muted">{t('paymentRefunded')}</dt>
                  <dd data-numeric className="text-danger">
                    −{money(payment.refundedAmount, locale, payment.currency)}
                  </dd>
                </>
              ) : null}
            </dl>
          ) : null}

          {payment && cap < row.amount ? (
            <Alert tone="info">
              {cap === 0 ? t('refundNothingLeft') : t('refundCappedByPayment', { left: money(cap, locale) })}
            </Alert>
          ) : null}

          {creditable && !creditOnly ? (
            // Where it goes: back the way it came, or to the customer's balance (ADR-081).
            <Choices
              legend={t('refundDestination')}
              name="return-refund-to"
              value={toCredit ? 'credit' : 'payment'}
              onChange={(next) => {
                setToCredit(next === 'credit');
                setTyped(null);
              }}
              options={[
                { value: 'payment', label: t('refundToPayment') },
                { value: 'credit', label: t('refundToCreditLabel'), hint: t('refundToCreditHint') },
              ]}
            />
          ) : null}

          <Field
            label={t('refundAmountLabel')}
            htmlFor="refund-amount"
            hint={t('refundAmountHint', { max: money(cap, locale) })}
            error={typed !== null ? amountError : undefined}
          >
            <Input
              id="refund-amount"
              dir="ltr"
              inputMode="decimal"
              autoComplete="off"
              value={value}
              invalid={typed !== null && !check.ok}
              onChange={(event) => setTyped(event.target.value)}
            />
          </Field>

          {viaCash ? (
            // No provider holds the money: say how it went back, so the books can find it.
            <>
              <Field label={t('refundMethodLabel')} htmlFor="refund-method">
                <Select
                  id="refund-method"
                  className="w-full"
                  value={method}
                  onChange={(event) => setMethod(event.target.value as RefundMethod | '')}
                >
                  <option value="">{t('dispositionPick')}</option>
                  {REFUND_METHODS.map((entry) => (
                    <option key={entry} value={entry}>
                      {t(`refundMethod${entry}` as MessageKey)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t('refundReference')} htmlFor="refund-reference" hint={t('refundReferenceHint')}>
                <Input id="refund-reference" value={reference} onChange={(event) => setReference(event.target.value)} />
              </Field>
            </>
          ) : null}

          {amount !== null && amount < row.amount ? (
            <p className="text-xs text-warning">{t('refundPartial', { full: money(row.amount, locale) })}</p>
          ) : null}

          {/* What will happen, in words, before the button is pressed — and again in the confirmation. */}
          {amount !== null && !orderLoading ? <p className="text-sm text-ink">{statement}</p> : null}

          {actionError ? <Alert>{actionError}</Alert> : null}
        </form>
      </Sheet>

      <ConfirmDialog
        open={confirming}
        title={t('refundConfirmTitle', { amount: shown })}
        body={statement}
        confirmLabel={t('refundConfirm', { amount: shown })}
        cancelLabel={t('cancel')}
        busy={saving}
        onConfirm={() => void confirm()}
        onCancel={() => setConfirming(false)}
      />
    </>
  );
}
